// Minimal typed REST client. Auth tokens are obtained via the
// Buttrbase flow (see `lib/auth.ts`) and attached as bearer.

export type Json = string | number | boolean | null | Json[] | { [k: string]: Json };

const API_BASE = ((import.meta as ImportMeta & { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE) || "/v1";
/** Same-origin Buttrbase control plane (CF Pages `functions/api`). */
const CONTROL_PLANE_BASE = "/api";
/** Ganvil Cloud application UUID on Buttrbase (OTP, OAuth, invites). */
export const GANVIL_APP_UUID = "c4de0a30-2462-48ad-b5a0-31b63486d920";

export class ApiError extends Error {
  constructor(public status: number, public body: Json) {
    super(`API ${status}`);
  }
}

/** Session persistence: survive a page refresh (SSR/prerender has no
 *  localStorage, hence the typeof guard). */
const BEARER_KEY = "ganvil_bearer";
const storedBearer =
  typeof localStorage !== "undefined"
    ? localStorage.getItem(BEARER_KEY) ?? undefined
    : undefined;
let bearer: string | undefined = storedBearer;
export function setBearer(token: string | undefined) {
  bearer = token;
  if (typeof localStorage !== "undefined") {
    if (token) localStorage.setItem(BEARER_KEY, token);
    else localStorage.removeItem(BEARER_KEY);
  }
}
/** Read-through for sibling clients (lib/ganforge.ts) so the session
 *  bearer is wired in exactly one place. */
export function getBearer(): string | undefined {
  return bearer;
}

/** Best-effort human message for an error thrown by `api`/`fetch`. The
 *  problem envelope is `{ type, title, status, detail }` (see gc-api). */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const b = e.body;
    if (b && typeof b === "object" && !Array.isArray(b)) {
      const detail = (b as { [k: string]: Json }).detail;
      if (typeof detail === "string" && detail) return detail;
      const title = (b as { [k: string]: Json }).title;
      if (typeof title === "string" && title) return title;
    }
    return `Request failed (HTTP ${e.status}).`;
  }
  if (e instanceof Error) return e.message;
  return "Something went wrong.";
}

export async function fetchWithRetry(url: string, init?: RequestInit, maxRetries = 6): Promise<Response> {
  let retries = 0;
  let delay = 1000; // Start with 1s delay
  
  while (true) {
    try {
      const res = await fetch(url, init);
      
      // Retry on gateway/unavailable errors that might indicate temporary drops
      if (res.status === 502 || res.status === 503 || res.status === 504) {
        throw new Error(`HTTP ${res.status}`);
      }
      
      return res;
    } catch (e) {
      if (retries >= maxRetries) {
        throw e;
      }
      
      // Calculate jitter to prevent thundering herd
      const jitter = Math.random() * 200;
      const totalDelay = delay + jitter;
      
      console.warn(`Network offline or unreachable. Retrying in ${Math.round(totalDelay)}ms... (Attempt ${retries + 1}/${maxRetries})`);
      
      await new Promise(r => setTimeout(r, totalDelay));
      
      retries++;
      // Exponential backoff
      delay = Math.min(delay * 2, 30000); // Max 30s delay
    }
  }
}

export async function api<T>(
  path: string,
  init: RequestInit & { retries?: number } = {},
): Promise<T> {
  const { retries = 6, ...rest } = init;
  const headers = new Headers(rest.headers);
  headers.set("Accept", "application/vnd.ganvil.v1+json");
  if (!headers.has("Content-Type") && rest.body) {
    headers.set("Content-Type", "application/json");
  }
  if (bearer) headers.set("Authorization", `Bearer ${bearer}`);
  const res = await fetchWithRetry(
    `${API_BASE}${path}`,
    { credentials: "same-origin", ...rest, headers },
    retries,
  );
  const text = await res.text();
  const body = text ? (JSON.parse(text) as Json) : null;
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

/** Buttrbase wraps most JSON as `{ data: T }`. Pass the parsed body through. */
export function unwrapData<T>(body: Json | null): T {
  if (body && typeof body === "object" && !Array.isArray(body) && "data" in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

/**
 * Call the Buttrbase control plane (`/api/...`).
 * Teams, invitations, environments, and org members live here — not on
 * search-svc `/v1`. Sends the same bearer `api()` uses.
 */
export async function controlPlane<T>(
  path: string,
  init: RequestInit & { retries?: number } = {},
): Promise<T> {
  const { retries = 1, ...rest } = init;
  const headers = new Headers(rest.headers);
  headers.set("Accept", "application/json");
  if (!headers.has("Content-Type") && rest.body) {
    headers.set("Content-Type", "application/json");
  }
  if (bearer) headers.set("Authorization", `Bearer ${bearer}`);
  const suffix = path.startsWith("/") ? path : `/${path}`;
  const res = await fetchWithRetry(
    `${CONTROL_PLANE_BASE}${suffix}`,
    { credentials: "same-origin", ...rest, headers },
    retries,
  );
  const text = await res.text();
  const body = text ? (JSON.parse(text) as Json) : null;
  if (!res.ok) throw new ApiError(res.status, body);
  return unwrapData<T>(body);
}
